import { formatTideHeight, formatTideTime, type TideData, type TideDatum } from "@/lib/conditions/tide-format";

function formatHour(h: number): string {
  if (h === 0 || h === 24) return "12am";
  if (h === 12) return "12pm";
  return h < 12 ? `${h}am` : `${h - 12}pm`;
}

function barColor(state: string, isBestTide: boolean): string {
  if (isBestTide) return "bg-emerald-400";
  switch (state) {
    case "high":
      return "bg-sky-400";
    case "low":
      return "bg-sky-200";
    case "rising":
      return "bg-sky-300";
    case "falling":
      return "bg-sky-300";
    default:
      return "bg-sky-300";
  }
}

export function TideChart({
  tide,
  tidePreference,
}: {
  tide: TideData;
  tidePreference: string | null;
}) {
  const displayPoints = tide.points.filter((p) => p.hour >= 5 && p.hour <= 20);
  if (displayPoints.length === 0) return null;
  // Bars and the preferred-tide highlight scale within the day's displayed
  // range whatever the datum. Detail entries cached before 27 September 2026
  // carry no datum; their heights are the old 0..3 m positions, so show them
  // unsigned. The lunar fallback ("none") was never computed for this place,
  // so its heights are not shown as numbers and its times are estimates.
  const datum: TideDatum = tide.datum ?? "low-water";
  const heightLabel = (heightM: number): string | null =>
    datum === "none" ? null : formatTideHeight(heightM, datum);
  const heights = tide.points.map((p) => p.heightM);
  const minHeight = Math.min(...heights);
  const heightRange = Math.max(...heights) - minHeight;
  const normalised = (heightM: number) =>
    heightRange > 0.05 ? (heightM - minHeight) / heightRange : 0.5;

  function isBestTideWindow(hour: number): boolean {
    if (!tidePreference || tidePreference === "all tides") return false;
    const point = tide.points.find((p) => p.hour === hour);
    if (!point) return false;
    const normHeight = normalised(point.heightM);

    if (tidePreference === "low" || tidePreference === "low-mid") {
      return normHeight < 0.4;
    }
    if (tidePreference === "mid" || tidePreference === "mid-high") {
      return normHeight >= 0.3 && normHeight <= 0.8;
    }
    if (tidePreference === "high") {
      return normHeight > 0.6;
    }
    return false;
  }

  return (
    <div className="bg-white rounded-[16px] border border-border-light p-5 md:p-7">
      <div className="flex items-baseline justify-between mb-1">
        <h3 className="text-[12px] font-semibold uppercase tracking-[1.2px] text-text-muted">
          Tide
        </h3>
        <span className="text-[11px] text-text-muted/70">
          {tide.isApproximate ? "Approximate model" : "Modelled tide"}
        </span>
      </div>
      {datum === "msl" && (
        <p className="text-[10px] text-text-muted/70 mb-1">Heights relative to mean sea level</p>
      )}
      {datum === "none" && (
        <p className="text-[10px] text-text-muted/70 mb-1">
          No local tide data for this spot. Times are estimated from the moon&apos;s phase only.
        </p>
      )}

      {tidePreference && tidePreference !== "all tides" && (
        <p className="text-[11px] text-text-muted mb-3">
          Best on {tidePreference} tide
          <span className="inline-block w-2 h-2 rounded-full bg-emerald-400 ml-1.5 align-middle" />
        </p>
      )}

      <div className="flex items-end gap-[2px] h-[72px] mt-2">
        {displayPoints.map((p) => {
          const pct = normalised(p.heightM) * 100;
          const best = isBestTideWindow(p.hour);
          const label = heightLabel(p.heightM);
          return (
            <div
              key={p.hour}
              className={`flex-1 min-w-0 rounded-t-[2px] transition-all ${barColor(p.state, best)}`}
              style={{ height: `${Math.max(pct, 6)}%` }}
              title={label ? `${formatHour(p.hour)}: ${label} (${p.state})` : `${formatHour(p.hour)}: ${p.state}`}
            />
          );
        })}
      </div>

      <div className="flex justify-between text-[9px] text-text-muted mt-1">
        <span>{formatHour(displayPoints[0].hour)}</span>
        <span>{formatHour(displayPoints[displayPoints.length - 1].hour)}</span>
      </div>

      <div className="flex flex-wrap gap-x-5 gap-y-1 mt-3">
        {tide.highTides
          .filter((ht) => ht.hour >= 5 && ht.hour <= 20)
          .map((ht, i) => (
            <div key={i} className="flex items-center gap-1.5 text-[11px]">
              <span className="w-2 h-2 rounded-full bg-sky-400" />
              <span className="font-semibold text-navy">
                High {formatTideTime(ht.hour)}
              </span>
              {heightLabel(ht.heightM) && (
                <span className="text-text-muted">{heightLabel(ht.heightM)}</span>
              )}
            </div>
          ))}
        {tide.lowTides
          .filter((lt) => lt.hour >= 5 && lt.hour <= 20)
          .map((lt, i) => (
            <div key={i} className="flex items-center gap-1.5 text-[11px]">
              <span className="w-2 h-2 rounded-full bg-sky-200" />
              <span className="font-semibold text-navy">
                Low {formatTideTime(lt.hour)}
              </span>
              {heightLabel(lt.heightM) && (
                <span className="text-text-muted">{heightLabel(lt.heightM)}</span>
              )}
            </div>
          ))}
      </div>
    </div>
  );
}
